<template>
  <div v-if="status && status.liberado" id="usuarioMcpChave" class="q-mt-lg">
    <div class="text-subtitle2">Acesso via MCP (DouraSat e outras ferramentas)</div>
    <div class="q-mt-xs">{{ rotuloEstado }}</div>
    <div class="text-caption text-grey-7">{{ rotuloUltimoUso }}</div>

    <q-select
      v-model="modo"
      :options="opcoesModo"
      emit-value
      map-options
      dense
      label="Modo"
      :hint="dicaModo"
      :disable="ocupado"
      class="q-mt-sm"
      style="max-width: 260px"
      @update:model-value="trocarModo"
    />

    <div class="q-mt-sm q-gutter-sm">
      <q-btn id="btnGerarChaveMcp" color="primary" no-caps :loading="ocupado" :disable="ocupado" :label="status.ativo ? 'Gerar nova chave' : 'Gerar chave'" @click="gerar" />
      <q-btn v-if="status.ativo" id="btnRevogarChaveMcp" flat color="negative" no-caps :loading="ocupado" :disable="ocupado" label="Revogar" @click="revogar" />
    </div>

    <q-dialog :model-value="!!chaveGerada" persistent>
      <q-card class="q-pa-md" style="max-width: 520px">
        <div class="text-subtitle1">Chave do MCP</div>
        <q-input :model-value="chaveGerada" readonly dense class="q-mt-sm" />
        <div class="text-negative q-mt-sm">Guarde agora: ela não será mostrada de novo.</div>
        <div class="text-caption q-mt-sm">
          No DouraSat, use o cabeçalho Authorization com o valor Bearer &lt;chave&gt; e a URL {{ urlMcp }}
        </div>
        <div class="q-mt-md q-gutter-sm">
          <q-btn color="primary" no-caps label="Copiar" @click="copiar" />
          <q-btn flat no-caps label="Fechar" @click="fecharChave" />
        </div>
      </q-card>
    </q-dialog>
  </div>
</template>

<script>
import axios from "@/axios-auth";
import { copyToClipboard } from "quasar";

export default {
  name: "usuario-mcp-chave",
  props: { userId: { type: Number, required: true } },
  data() {
    return { status: null, modo: "leitura", chaveGerada: "", ocupado: false };
  },
  computed: {
    opcoesModo() {
      const semEscrita = !(this.status && this.status.escrita_disponivel);
      return [
        { label: "Somente leitura", value: "leitura" },
        {
          label: "Leitura e escrita",
          value: "leitura_escrita",
          disable: semEscrita,
          description: semEscrita ? "Ainda não há ações liberadas via MCP" : "",
        },
      ];
    },
    dicaModo() {
      return this.status && this.status.escrita_disponivel ? "" : "Ainda não há ações liberadas via MCP";
    },
    rotuloEstado() {
      if (!this.status.ativo) return "Desligado";
      return this.status.modo === "leitura_escrita" ? "Ativo — Leitura e escrita" : "Ativo — Somente leitura";
    },
    rotuloUltimoUso() {
      if (!this.status.ultimo_uso) return "Nunca usado";
      const d = new Date(this.status.ultimo_uso);
      const p = (n) => String(n).padStart(2, "0");
      return `Último uso: ${p(d.getDate())}/${p(d.getMonth() + 1)}/${d.getFullYear()} ${p(d.getHours())}:${p(d.getMinutes())}`;
    },
    urlMcp() {
      return `${process.env.VUE_APP_URL_API}/mcp`;
    },
  },
  watch: {
    userId: {
      immediate: true,
      handler() {
        this.carregar();
      },
    },
  },
  methods: {
    async carregar() {
      // Zera antes da resposta: trocar de usuário não pode deixar o bloco do anterior clicável.
      this.status = null;
      try {
        const res = await axios.get(`/users/mcp/${this.userId}`);
        this.status = res && res.data && res.data.success ? res.data.data : null;
        if (this.status && this.status.modo) this.modo = this.status.modo;
      } catch (e) {
        this.status = null;
      }
    },
    confirmar(mensagem) {
      return new Promise((resolve) =>
        this.$q
          .dialog({ title: "Confirmar", message: mensagem, cancel: true })
          .onOk(() => resolve(true))
          .onCancel(() => resolve(false))
      );
    },
    async gerar() {
      if (this.ocupado) return;
      if (this.status.ativo && !(await this.confirmar("A chave atual vai parar de funcionar. Continuar?"))) return;
      this.ocupado = true;
      try {
        const res = await axios.post(`/users/mcp/${this.userId}`, { acao: "gerar", modo: this.modo });
        if (!res || !res.data || !res.data.success) return this.avisarErro(res);
        this.chaveGerada = res.data.data.chave;
        this.status = { ...this.status, ativo: true, modo: res.data.data.modo, ultimo_uso: null };
      } finally {
        this.ocupado = false;
      }
    },
    async revogar() {
      if (this.ocupado) return;
      if (!(await this.confirmar("A chave vai parar de funcionar imediatamente. Revogar?"))) return;
      this.ocupado = true;
      try {
        const res = await axios.post(`/users/mcp/${this.userId}`, { acao: "revogar" });
        if (!res || !res.data || !res.data.success) return this.avisarErro(res);
        this.status = { ...this.status, ...res.data.data };
      } finally {
        this.ocupado = false;
      }
    },
    async trocarModo(modo) {
      if (!this.status.ativo || this.ocupado) return;
      this.ocupado = true;
      try {
        const res = await axios.post(`/users/mcp/${this.userId}`, { acao: "modo", modo });
        if (!res || !res.data || !res.data.success) {
          this.modo = this.status.modo;
          return this.avisarErro(res);
        }
        this.status = { ...this.status, ...res.data.data };
      } finally {
        this.ocupado = false;
      }
    },
    avisarErro(res) {
      // axios-auth resolve erro de HTTP como { error }; a mensagem do backend fica em error.response.data.
      const msg =
        (res && res.data && res.data.message) ||
        (res && res.error && res.error.response && res.error.response.data && res.error.response.data.message);
      this.$q.notify({ message: msg || "Não foi possível concluir a operação.", color: "orange", position: "top-right" });
    },
    copiar() {
      copyToClipboard(this.chaveGerada);
    },
    fecharChave() {
      this.chaveGerada = "";
    },
  },
};
</script>
